import {ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';

export type ColorName = 'red' | 'yellow' | 'green' | 'grey';
export type ColorCode = '#ED323A' | '#DEC24D' | '#3BC458' | '#A7A7AE';

export const colors: { [color in ColorName]: ColorCode } = {
  red: '#ED323A',
  yellow: '#DEC24D',
  green: '#3BC458',
  grey: '#A7A7AE'
};

export interface StatusConfiguration {
  tooltip: string;
  color: ColorCode;
  isFlickering: boolean;
}

export type StatusConfigurations<T extends string> = Map<T, StatusConfiguration>;

@Component({
  selector: 'app-status',
  templateUrl: './status.component.html',
  styleUrls: ['./status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatusComponent implements OnInit, OnChanges {
  @Input() status: string;
  @Input() tooltip: string = undefined;
  @Input() public configurations: StatusConfigurations<string> | undefined = undefined;
  public tooltipText: string;
  public isFlickeringDot: boolean = false;
  public color: ColorCode = colors.grey;

  private colors = {
    deleted: colors.red,
    failed: colors.red,
    disabled: colors.red,
    progress: colors.red,
    waiting: colors.yellow,
    creating: colors.yellow,
    deleting: colors.yellow,
    complete: colors.green,
    active: colors.green,
    banned: colors.yellow,

    failedDVR: colors.red,
    waitingDVR: colors.yellow,
    startingDVR: colors.yellow,
    recordingDVR: colors.red,
    stoppingDVR: colors.yellow,
    completeDVR: colors.green,
    completedDVR: colors.green,

    all: colors.green,
    some: colors.yellow,
    none: colors.grey,

    online: colors.green,
    offline: colors.red,

    launching: colors.yellow,
    available: colors.green,
    error: colors.red,
    'pending-associate': colors.yellow
  };

  private tooltips = {
    deleted: 'Deleted',
    failed: 'Failed',
    disabled: 'Disabled',
    progress: 'In progress',
    waiting: 'Waiting',
    creating: 'Creating',
    deleting: 'Deleting',
    complete: 'Completed',
    active: 'Active',
    banned: 'Banned',

    failedDVR: 'Record failed',
    waitingDVR: 'Waiting for incoming stream',
    startingDVR: 'Record starting',
    recordingDVR: 'Recording',
    stoppingDVR: 'Record completing',
    completedDVR: 'Completed',
    completeDVR: 'Completed',

    launching: 'Launching',
    available: 'Available',
    error: 'Error',
    'pending-associate': 'Pending Associate',
    'no-associate': 'No Associate'
  };

  public ngOnInit(): void {
    this.setTooltipText();
    this.setColor();
    this.setIsFlickeringDot();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['tooltip']) {
      this.setTooltipText();
    }
    if (changes['status']) {
      this.setTooltipText();
      this.setColor();
      this.setIsFlickeringDot();
    }
  }

  private setColor(): void {
    if (this.configurations) {
      const configuration: StatusConfiguration | undefined = this.configurations.get(this.status);
      if (configuration) {
        this.color = configuration.color;
        return;
      }
    }

    this.color = this.colors[this.status];
  }

  private setTooltipText(): void {
    if (this.tooltip) {
      this.tooltipText = this.tooltip;
      return;
    }

    if (this.configurations) {
      const configuration: StatusConfiguration | undefined = this.configurations.get(this.status);
      if (configuration) {
        this.tooltipText = configuration.tooltip;
        return;
      }
    }

    this.tooltipText = this.tooltips[this.status];
  }

  private setIsFlickeringDot(): void {
    if (this.configurations) {
      const configuration: StatusConfiguration | undefined = this.configurations.get(this.status);
      if (configuration) {
        this.isFlickeringDot = configuration.isFlickering;
        return;
      }
    }

    this.isFlickeringDot =
      this.status === 'waitingDVR' ||
      this.status === 'startingDVR' ||
      this.status === 'recordingDVR' ||
      this.status === 'stoppingDVR' ||
      this.status === 'launching' ||
      this.status === 'pending-associate';
  }
}
